import React, { useCallback, useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Button from 'react-bootstrap/Button';
import { useToasts } from 'react-toast-notifications';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Step3Phone from '../images/nstep3phone.png';
import {
    userLogin as userLoginRules,
    bio as bioRules,
    alphaNumDashSpace
} from '../utils/rules';
import Countries from './countries';
import VerificationCodeInput from "./VerificationCodeInput";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import debounce from 'lodash/debounce';
import { captureMessage } from '@sentry/react';
import { useDispatch } from 'react-redux';
import { setUser } from '../../redux/actions';

const EditProfileForm = ({ user, showHeader, getUserInformation }) => {
    const { addToast } = useToasts();
    const { executeRecaptcha } = useGoogleReCaptcha();
    const dispatch = useDispatch();

    const [code, setCode] = useState('');
    const [emailErrorOnSendCode, setEmailErrorOnSendCode] = useState('');
    const [editEmail, setEditEmail] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [showPanelHeader, setShowPanelHeader] = useState(true);
    useEffect(() => {
        if (user && Object.keys(user).length > 0) {
            fillForm(user);
        }
    }, [user]);

    useEffect(() => {
        if (typeof showHeader === 'undefined') {
            setShowPanelHeader(true);
        } else {
            setShowPanelHeader(showHeader);
        }
    }, []);

    const [isLoading, setIsLoading] = useState(false);

    const handleCodeChange = (value) => {
        formik.setFieldValue('code', value);
        setCode(value);
    };

    const formik = useFormik({
        initialValues: {
            showVerificationCode: editEmail,
            login: '',
            email: '',
            firstname: '',
            lastname: '',
            bio: '',
            website: '',
            location: '',
            dob: '',
            gender: '',
            code: code
        },
        validationSchema: Yup.object({
            showVerificationCode: Yup.boolean(),
            login: Yup.string()
                .min(userLoginRules.minLength, `Mininum ${userLoginRules.minLength} characters`)
                .max(userLoginRules.maxLength, `Maximum ${userLoginRules.maxLength} characters`)
                .matches(
                    userLoginRules.regex,
                    'Only alpha numeric and - characters are allowed',
                )
                .required('Login name is required')
                .notOneOf(userLoginRules.invalid, 'Forbidden user login'),
            email: Yup.string()
                .email('Invalid email format')
                .when("showVerificationCode", {
                    is: (showVerificationCode) => showVerificationCode === true,
                    then: Yup.string()
                        .required('Email is required')
                }),
            firstname: Yup.string()
                .matches(
                    alphaNumDashSpace.regex,
                    'First name can only use letters, numbers, space, - and _',
                ),
            lastname: Yup.string()
                .matches(
                    alphaNumDashSpace.regex,
                    'Last name can only use letters, numbers, space, - and _',
                ),
            bio: Yup.string()
                // .required('Bio is required')
                .max(bioRules.maxLength, `Maximum ${bioRules.maxLength} characters`),
            website: Yup.string(),
            location: Yup.string()
                .required('Location is required.'),
            dob: Yup.date().typeError('Please enter a valid date'),
            gender: Yup.mixed().oneOf([0, 1, '0', '1'], 'Gender is required'),
            code: Yup.number()
                .when("showVerificationCode", {
                    is: (showVerificationCode) => showVerificationCode === true,
                    then: Yup.number()
                        .typeError('Invalid email verification code')
                        .required('Email verification code required')
                })
        }),
        onSubmit: async (values, { setErrors }) => {
            setIsLoading(true);
            const params = {
                login: values.login,
                email: values.email,
                firstname: values.firstname,
                lastname: values.lastname,
                bio: values.bio,
                website: values.website,
                location: values.location,
                dob: values.dob,
                gender: values.gender
            };

            if (editEmail) {
                params.code = values.code;
            }

            await axios.patch(`/api/users/${user.id}`, params)
                .then((response) => {
                    setCode('');
                    setEditEmail(false);
                    // getUserInformation();
                    dispatch(setUser());
                    addToast('Information successfully updated', { appearance: 'success', autoDismiss: true });
                })
                .catch((error) => {
                    console.log({ error });
                    if (error.response.status === 422) {
                        Object.keys(error.response.data.errors).forEach((key) => {
                            setErrors({
                                [key]: error.response.data.errors[key][0],
                            });
                        });
                    }
                    addToast('Unable to update information', { appearance: 'error', autoDismiss: true });
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
    });

    useEffect(() => {
        formik.setFieldValue('showVerificationCode', editEmail);
    }, [editEmail]);

    const fillForm = (userInformation) => {
        Object.keys(formik.values).forEach((key) => {
            if (userInformation[key]) {
                formik.setFieldValue(key, userInformation[key], false);
            }
        });

        if (userInformation.creator && Object.keys(userInformation.creator).length > 0) {
            Object.keys(formik.values).forEach((key) => {
                if (userInformation.creator[key]) {
                    formik.setFieldValue(key, userInformation.creator[key], false);
                }
            });
        }
    };

    const handleClear = () => {
        const fieldsToClear = [
            'firstname',
            'lastname',
            'bio',
            'dob',
            'website',
            'gender',
        ];

        fieldsToClear.forEach((field) => {
            formik.setFieldValue(field, '');
        });
    };

    const editEmailAddress = () => {
        setEditEmail(true);
    };

    const revertEmailAddress = () => {
        setCode('');
        setEditEmail(false);
        formik.setFieldValue('email', user.email);
        formik.setFieldValue('code', '');
    };

    const handleEmailChange = (e) => {
        formik.setFieldValue('email', e.target.value, false);
        setEmailError('');
    };

    const checkEmail = useCallback(
        debounce(async (value, username) => {
            if (value === '') {
                return;
            }

            await axios.post('/api/validate/email', {
                email: value,
                username: username
            }).then((response) => {
                formik.setErrors({ email: '' });
                setEmailError('');
            }).catch((error) => {
                captureMessage(error);
                if (error.response && error.response.status === 422) {
                    setEmailError(error.response.data.errors.email[0]);
                    formik.setErrors({ email: error.response.data.errors.email[0] });
                }
            });
        }, 500),
        [],
    );

    const handleEmailFocusOut = (e) => {
        checkEmail(e.target.value, formik.values.login);
    };

    return (
        <div className="panel panel-default">
            {showPanelHeader && (
                <div className="panel-heading refpayout">
                    <span className="BC-head-ico">
                        <LazyLoadImage src={Step3Phone} />
                    </span>
                    <span>
                        Step 3 - Basic Profile Details
                    </span>
                </div>
            )}

            <div className="panel-body">
                <form onSubmit={formik.handleSubmit}>
                    <div className="profile-form-control-wrapper">
                        <div className="profile-form-control">
                            <div className="profile-form-label">
                                <label>
                                    Username
                                    <span className="required">*</span>
                                </label>
                            </div>
                            <div className="profile-form-input">
                                <input
                                    type="text"
                                    id="login"
                                    name="login"
                                    placeholder="Login Name"
                                    className={`form-control${formik.errors.login && formik.touched.login ? ' is-invalid' : ''}`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.login}
                                />
                                <p className="profile-field-description">
                                    {process.env.MIX_APP_URL}
                                    /
                                    {formik.values.login}
                                </p>
                                {formik.errors.login && formik.touched.login ? <div className="invalid-feedback">{formik.errors.login}</div> : null}
                            </div>
                        </div>
                        <div className="profile-form-control">
                            <div className="profile-form-label">
                                <label>
                                    Email address
                                    <span className="required">*</span>
                                </label>
                            </div>
                            <div className="profile-form-input">
                                <div className="edit-email-input-container">
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="Email"
                                        id="email"
                                        className={`form-control${formik.errors.email && formik.touched.email ? ' is-invalid' : ''}`}
                                        onChange={editEmail ? handleEmailChange : () => { }}
                                        onBlur={editEmail ? handleEmailFocusOut : () => { }}
                                        value={formik.values.email}
                                        disabled={!editEmail}
                                    />
                                    <a
                                        type={`button`}
                                        className={`edit-email-btn`}
                                        onClick={editEmail ? revertEmailAddress : editEmailAddress}
                                    >
                                        {editEmail ? 'Cancel' : 'Edit'}
                                    </a>
                                </div>
                                {
                                    emailErrorOnSendCode
                                        ? <div className="invalid-feedback">{emailErrorOnSendCode}</div>
                                        : (
                                            formik.errors.email && formik.touched.email
                                                ? <div className="invalid-feedback">{formik.errors.email}</div>
                                                : null
                                        )
                                }
                            </div>
                        </div>
                        {
                            editEmail
                                ? <div className={'profile-form-control'}>
                                    <div className={'profile-form-label'}>
                                        <label>
                                            Verification Code
                                            <span className="required">*</span>
                                        </label>
                                    </div>
                                    <div className={'profile-form-input'}>
                                        <VerificationCodeInput
                                            toVerify={formik.values.email}
                                            executeRecaptcha={executeRecaptcha}
                                            code={code}
                                            setCode={handleCodeChange}
                                            onEditProfileForm={true}
                                            // apiError={formik.errors.code && formik.touched.code ? formik.errors.code : null}
                                            emailError={emailError ?? formik.errors.email}
                                            verificationError={formik.errors.code && formik.touched.code ? formik.errors.code : null}
                                            hasEmail={formik.values.email.length}
                                            // emailErrorOnSendCode={emailErrorOnSendCode}
                                            setEmailErrorOnSendCode={setEmailErrorOnSendCode}
                                        />
                                    </div>
                                </div>
                                : null
                        }
                        <div className="profile-form-control">
                            <div className="profile-form-label">
                                <label>First name</label>
                            </div>
                            <div className="profile-form-input">
                                <input
                                    type="text"
                                    name="firstname"
                                    placeholder="First Name"
                                    id="firstname"
                                    className={`form-control${formik.errors.firstname && formik.touched.firstname ? ' is-invalid' : ''}`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.firstname}
                                />
                                {formik.errors.firstname && formik.touched.firstname ? <div className="invalid-feedback">{formik.errors.firstname}</div> : null}
                            </div>
                        </div>
                        <div className="profile-form-control">
                            <div className="profile-form-label">
                                <label>Last name</label>
                            </div>
                            <div className="profile-form-input">
                                <input
                                    type="text"
                                    name="lastname"
                                    placeholder="Last Name"
                                    id="lastname"
                                    className={`form-control${formik.errors.lastname && formik.touched.lastname ? ' is-invalid' : ''}`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.lastname}
                                />
                                {formik.errors.lastname && formik.touched.lastname ? <div className="invalid-feedback">{formik.errors.lastname}</div> : null}
                            </div>
                        </div>
                        {user.role !== 0 &&
                            <>
                                <div className="profile-form-control">
                                    <div className="profile-form-label">
                                        <label>Website URL</label>
                                    </div>
                                    <div className="profile-form-input">
                                        <input
                                            type="text"
                                            name="website"
                                            id="website"
                                            className={`form-control${formik.errors.website && formik.touched.website ? ' is-invalid' : ''}`}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.website}
                                        />
                                        {formik.errors.website && formik.touched.website ? <div className="invalid-feedback">{formik.errors.website}</div> : null}
                                    </div>
                                </div>

                                <div className="profile-form-control form-control-with-desc">
                                    <div className="profile-form-label">
                                        <label>Bio</label>
                                    </div>
                                    <div className="profile-form-input">
                                        <textarea
                                            name="bio"
                                            rows="5"
                                            className={`form-control${formik.errors.bio && formik.touched.bio ? ' is-invalid' : ''}`}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.bio}
                                        />
                                        <p className="profile-field-description">
                                            (
                                            {formik.values.bio.length}
                                            /1000 characters)
                                        </p>
                                        {formik.errors.bio && formik.touched.bio ? <div className="invalid-feedback">{formik.errors.bio}</div> : null}
                                    </div>
                                </div>
                                <div className="profile-form-control">
                                    <div className="profile-form-label">
                                        <label>D.O.B.</label>
                                    </div>
                                    <div className="profile-form-input">
                                        <input
                                            type="date"
                                            name="dob"
                                            id="dob"
                                            className={`form-control${formik.errors.dob && formik.touched.dob ? ' is-invalid' : ''}`}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            value={formik.values.dob}
                                        />
                                        {formik.errors.dob && formik.touched.dob ? <div className="invalid-feedback">{formik.errors.dob}</div> : null}
                                    </div>
                                </div>
                            </>
                        }

                        <div className="profile-form-control">
                            <div className="profile-form-label">
                                <label>Location</label>
                            </div>
                            <div className="profile-form-input">
                                <Countries
                                    name="location"
                                    id="location"
                                    className={`form-control${formik.errors.location && formik.touched.location ? ' is-invalid' : ''}`}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.location}
                                />
                                {formik.errors.location && formik.touched.location ? <div className="invalid-feedback">{formik.errors.location}</div> : null}
                            </div>
                        </div>

                        {user.role !== 0 &&
                            <div className="profile-form-control">
                                <div className="profile-form-label">
                                    <label>Gender</label>
                                </div>
                                <div className="profile-form-input gender-form-input">
                                    <span>
                                        <input
                                            type="radio"
                                            name="gender"
                                            id="gender-1"
                                            className={`form-control${formik.errors.gender && formik.touched.gender ? ' is-invalid' : ''}`}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            checked={['1', 1].includes(formik.values.gender)}
                                            defaultValue="1"
                                        />
                                        {' '}
                                        Male
                                    </span>
                                    <span>
                                        <input
                                            type="radio"
                                            name="gender"
                                            id="gender-2"
                                            className={`form-control${formik.errors.gender && formik.touched.gender ? ' is-invalid' : ''}`}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                            defaultValue="0"
                                            checked={['0', 0].includes(formik.values.gender)}
                                        />
                                        {' '}
                                        Female
                                    </span>
                                    {formik.errors.gender && formik.touched.gender ? <div className="invalid-feedback">{formik.errors.gender}</div> : null}
                                </div>
                            </div>
                        }

                    </div>
                    <div className="profile-form-button-container">
                        <div className="profile-form-button">
                            <button type="button" className="theme-btn theme-btn-bordered" onClick={handleClear}>Clear</button>
                            <Button
                                type="submit"
                                disabled={isLoading}
                                className="theme-btn theme-btn-bordered"
                            >
                                {isLoading ? 'Processing' : 'Save'}
                            </Button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditProfileForm;
